/**
 * 節點創建和夥伴回應的共用邏輯
 * 封裝 CoreForumNodeCreator 的核心邏輯，供其他組件複用
 */

import { newNode, newEdge } from './ideaTool';
import { triggerPartnerResponse } from './partnerResponseTrigger';
import { GroupContextTypes } from '../types/groupContext.types.js';

/**
 * 創建節點並觸發夥伴回應
 * @param {Object} params - 參數對象
 * @param {Object} params.ideaData - 節點數據 {title, content, tags, author, groupId}
 * @param {string} params.activityId - 活動 ID
 * @param {string} params.groupId - 群組 ID
 * @param {Object} params.ws - WebSocket 連接
 * @param {Array} params.nodes - 當前所有節點
 * @param {Array} params.edges - 當前所有邊
 * @param {Function} params.buildGroupContext - Context 構建函數
 * @param {string} params.mode - 模式 ('chat', 'sticker', 'tagged')
 * @param {string} [params.parentNodeId] - 父節點 ID（回覆模式）
 * @param {boolean} [params.isNodePlanMode=false] - 是否為 nodePlan 模式
 * @param {Object} [params.nodePlan] - nodePlan 對象（nodePlan 模式時使用）
 * @param {number} [params.limit=15] - 對話歷史限制數量
 * @param {boolean} [params.validateContext=true] - 是否驗證 context
 * @param {Function} [params.onSuccess] - 成功回調
 * @param {Function} [params.onError] - 錯誤回調
 * @returns {Promise<Object>} 返回 {success: boolean, nodeId: string, error?: any}
 */
export const createNodeWithPartnerResponse = async ({
  ideaData,
  activityId,
  groupId,
  ws,
  nodes,
  edges,
  buildGroupContext,
  mode,
  parentNodeId = null,
  isNodePlanMode = false,
  nodePlan = null,
  limit = 15,
  validateContext = true,
  onSuccess = null,
  onError = null
}) => {
  try {
    // 1. 創建新節點
    const responseFromPostNode = await newNode(ideaData, activityId, ws);
    const newNodeId = responseFromPostNode?.data?.node?.id;
    
    if (!newNodeId) {
      throw new Error('Failed to get new node ID from response');
    }

    // 2. 創建連線邏輯
    // nodePlan 模式：使用 nodePlan.newEdge 決定是否創建連線
    if (isNodePlanMode && nodePlan?.newEdge && nodePlan.nodeId) {
      const edgeData = {
        groupId: groupId,
        from: newNodeId,
        to: nodePlan.nodeId,
      };
      await newEdge(edgeData, activityId, ws);
    }
    // 一般模式：如果有父節點，創建連線
    else if (!isNodePlanMode && parentNodeId) {
      const edgeData = {
        groupId: groupId,
        from: newNodeId,
        to: parentNodeId,
      };
      await newEdge(edgeData, activityId, ws);
    }

    // 3. 構建 Context 並觸發夥伴回應
    if (buildGroupContext && typeof buildGroupContext === 'function') {
      const currentContent = `${ideaData.title}\n\n${ideaData.content}`;
      
      // 決定追溯起點（CoreForumNodeCreator 的邏輯）
      let graphNodesForContext;
      
      if (parentNodeId || (isNodePlanMode && nodePlan?.newEdge && nodePlan?.nodeId)) {
        // 回覆模式：從父節點追溯
        const traceFromNodeId = parentNodeId || nodePlan.nodeId;
        graphNodesForContext = nodes.map(node => ({
          ...node,
          _currentNodeId: traceFromNodeId,
          _edges: edges
        }));
        console.log(`[nodeCreationHelper] 從父節點追溯: ${traceFromNodeId}, edges: ${edges.length}`);
      } else {
        // 新 START 節點：使用現有的 graphNodes
        graphNodesForContext = nodes.map(node => ({
          ...node,
          _edges: edges
        }));
        console.log(`[nodeCreationHelper] 新 START 節點，使用現有對話歷史, edges: ${edges.length}`);
      }
      
      console.log(`[nodeCreationHelper] 構建 context 前:`, {
        mode,
        nodeCount: graphNodesForContext.length,
        edgeCount: edges.length,
        hasParent: !!parentNodeId
      });
      
      // 統一接口：buildGroupContext(graphNodes, currentContent, tag)
      const groupContext = buildGroupContext(
        graphNodesForContext,
        currentContent,
        ideaData.tags
      );
      
      // ✅ 強類型驗證 groupContext（如果啟用）
      if (validateContext) {
        const validation = GroupContextTypes.validateDetailed(groupContext);
        if (!validation.isValid) {
          console.error('[nodeCreationHelper] buildGroupContext 返回的 groupContext 驗證失敗', {
            mode,
            issues: validation.issues,
            score: validation.score,
            groupContext
          });
          const error = new Error(
            `系統錯誤：群組上下文驗證失敗\n問題：${validation.issues.join(', ')}\n完整性評分：${(validation.score * 100).toFixed(0)}%`
          );
          if (onError) onError(error);
          return {
            success: false,
            error: error
          };
        }
        
        console.log('[nodeCreationHelper] buildGroupContext 驗證通過:', {
          mode,
          score: validation.score,
          conversationHistoryLength: groupContext.conversationHistory.length,
          conversationHistory: groupContext.conversationHistory.map(h => ({
            id: h.id,
            author: h.author,
            title: h.title?.substring(0, 50)
          }))
        });
      }
      
      console.log(`[nodeCreationHelper] 構建 context 後:`, {
        mode,
        hasContext: !!groupContext,
        hasHistory: !!groupContext?.conversationHistory,
        historyLength: groupContext?.conversationHistory?.length || 0,
        contextVersion: groupContext?.contextVersion
      });

      // 4. 觸發夥伴回應（背景執行）
      triggerPartnerResponse({
        userNodeId: newNodeId,
        title: ideaData.title,
        content: currentContent,
        author: ideaData.author,
        ws: ws,
        activityId: activityId,
        groupId: groupId,
        mode: mode,
        groupContext: groupContext,
        partnerIds: [],
        parentTag: isNodePlanMode ? undefined : ideaData.tags
      }).then(partnerResponseResult => {
        if (partnerResponseResult.success) {
          console.log(`夥伴回應觸發成功 (${mode} Mode)`);
          if (partnerResponseResult.failedCount > 0) {
            console.warn(`部分夥伴回應失敗: ${partnerResponseResult.failedCount} 個`);
          }
        } else {
          console.warn(`夥伴回應觸發失敗 (${mode} Mode):`, partnerResponseResult.reason);
        }
      }).catch(error => {
        console.error('夥伴回應處理異常:', error);
      });
    } else {
      console.warn('[nodeCreationHelper] 未提供 buildGroupContext 函數，跳過夥伴回應');
    }

    if (onSuccess) onSuccess(newNodeId);
    
    return {
      success: true,
      nodeId: newNodeId
    };
  } catch (error) {
    console.error('[nodeCreationHelper] Error:', error);
    if (onError) onError(error);
    return {
      success: false,
      error: error
    };
  }
};
